import { useState, useMemo, useCallback, useRef, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import { App } from "antd";
import { getImportanceOptions } from "@/api/importances";
import { getStatusOptions } from "@/api/statuses";
import { getTaskTypeOptions } from "@/api/taskTypes";
import { getSprintsOptions } from "@/api/sprints";
import { getUnitEmployees, getEmployeesOptions } from "@/api/employees";
import {
  getEmployeeUnitsOptions,
  getUnitsOptions,
  getItem as getUnit,
  getUnitsTree,
} from "@/api/units";

import { FILTER_NAMES, getLocalStorageKey, NOT_FILLED_OPTION } from "@/constants/tasks";

/**
 * Хук useTaskFilters
 *
 * Назначение:
 * Управляет состоянием фильтров на странице задач. Обеспечивает применение фильтров как в реальном времени,
 * так и через модальное окно. Синхронизирует фильтры с состоянием таблицы, сохраняет их в localStorage,
 * управляет параметрами URL и инициализирует данные при загрузке компонента.
 *
 * Основные функции:
 * 1. fetchOptions - инициализация всех данных для фильтров (статусы, приоритеты, типы задач, РГ, спринты, сотрудники)
 * 2. fetchUserList - получение списка сотрудников (для выбранной РГ или всех)
 * 3. fetchSprintsList - получение списка спринтов (для выбранной РГ или всех)
 * 4. updateFilters - базовая функция обновления фильтров с нормализацией значений и сохранением
 * 5. handleFilterChange - изменение фильтра напрямую (со сбросом зависимых фильтров)
 * 6. handleTempFilterChange - изменение временных фильтров в модальном окне
 * 7. openFiltersModal - открыть модальное окно с фильтрами
 * 8. applyFilters - применить выбранные в модалке фильтры
 * 9. clearFilters - сбросить все фильтры на начальное значение
 * 10. getFlatUnits - плоский список всех РГ из дерева
 * 11. isAnyFilterApplied - флаг наличия активных фильтров
 *
 * Состояние возвращаемые хуком:
 * - statuses, priorities, taskTypes, sprints, employees, units, unitsTree - данные для выпадающих списков фильтров
 * - unitData - данные текущей РГ (если выбран раздел в РГ)
 * - optionsPending - флаг загрузки данных
 * - tempValueFilters - временные фильтры, изменяемые в модалке
 * - isFiltersModalShow - видимость модального окна фильтров
 *
 * Логика зависимостей между фильтрами:
 * - При смене рабочей группы (unit_uuid) сбрасываются исполнитель, автор и спринт, обновляется список сотрудников и список спринтов
 * - При смене типа доступа (access_level) пересчитываются рабочие группы (root/readOnly)
 * - При смене рабочих групп пользователя (units_uuids) сбрасывается тип доступа
 * - Поддержка фильтрации по URL параметрам (status, executor, copy_transaction_id)
 *
 * @param {string} unit_uuid - UUID текущей рабочей группы
 * @param {boolean} isAdmins - является ли пользователь администратором
 * @param {string} userUuid - UUID текущего пользователя
 * @param {Object} initialTableParams - начальные параметры таблицы (для сброса)
 * @param {URLSearchParams} searchParams - параметры поиска из URL
 * @param {Object} location - объект location из react-router
 * @param {Object} tableParams - текущие параметры таблицы с фильтрами
 * @param {Function} setParams - функция сохранения параметров (с записью в localStorage)
 * @param {Function} fetchData - функция загрузки данных с новыми параметрами
 * @param {Function} clearQueryParam - функция удаления параметров из URL (опционально)
 *
 * @returns {Object} Объект с функциями и состоянием управления фильтрами
 */
export const useTaskFilters = ({
  unit_uuid,
  isAdmins,
  userUuid,
  initialTableParams,
  searchParams,
  location,
  tableParams,
  setParams,
  fetchData,
  clearQueryParam,
}) => {
  const { message } = App.useApp();
  const navigate = useNavigate();

  const suppressNextFetch = useRef(false);
  const didInit = useRef(false);

  // Данные рабочей группы (если список задач в рамках РГ)
  const [unitData, setUnitData] = useState(null);

  // Списки для фильтров
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [unitsTree, setUnitsTree] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [optionsPending, setOptionsPending] = useState(true);

  // Окно с фильтрами
  const [isFiltersModalShow, setIsFiltersModalShow] = useState(false);

  const [tempValueFilters, setTempValueFilters] = useState(null);

  //  initial fetch
  useEffect(() => {
    if (suppressNextFetch.current) {
      // подавляем один вызов fetchOptions из-за navigate/очистки query
      suppressNextFetch.current = false;
      return;
    }
    fetchOptions().then(() => (didInit.current = true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit_uuid, isAdmins, location.search]);

  const fetchOptions = async () => {
    setOptionsPending(true);
    try {
      const [
        statusesRes,
        prioritiesRes,
        taskTypesRes,
        { units: unitsRes },
        unitRes,
        treeRes,
      ] = await Promise.all([
        getStatusOptions(),
        getImportanceOptions(),
        getTaskTypeOptions(),
        isAdmins ? getUnitsOptions() : getEmployeeUnitsOptions(),
        unit_uuid && getUnit(unit_uuid),
        unit_uuid
          ? getUnitsTree({ unit_uuid, from_unit: true })
          : getUnitsTree({ from_unit: true }),
      ]);

      setStatuses(statusesRes.map((i) => ({ ...i, label: i.name })));
      setPriorities(prioritiesRes.map((i) => ({ ...i, label: i.name })));
      setTaskTypes(taskTypesRes.map((i) => ({ ...i, label: i.name })));
      setUnits(unitsRes);
      setUnitsTree(treeRes);
      setUnitData(unitRes);

      // Инициализация параметров из localStorage
      let localParams =
        JSON.parse(
          localStorage.getItem(
            getLocalStorageKey(userUuid, unit_uuid, isAdmins),
          ),
        ) || initialTableParams;

      // FIXME: переписать потом под другие фильтры (более универсально)
      const paramsStatus = searchParams.get("status");
      const paramsExecutor = searchParams.get("executor");
      const paramsTasksUuids = searchParams.getAll(FILTER_NAMES.TASKS_UUIDS);
      const paramsCopyTx = searchParams.get(FILTER_NAMES.COPY_TRANSACTION_ID);

      if (paramsStatus || paramsExecutor) {
        localParams = {
          ...initialTableParams,
          filters: {
            ...initialTableParams.filters,
            ...(paramsExecutor && {
              responsible_executor_uuid: paramsExecutor,
            }),
            ...(paramsStatus && { status_uuid: [paramsStatus] }),
          },
        };
        navigate(window.location.pathname, { replace: true });
      }

      if (paramsCopyTx) {
        localParams = {
          ...initialTableParams,
          filters: {
            ...initialTableParams.filters,
            copy_transaction_id: paramsCopyTx,
          },
        };
        // Не чистим url, потому что транзакция не сохраняется в LS и сбросится
      }

      if (paramsTasksUuids?.length > 0) {
        localParams = {
          ...initialTableParams,
          filters: {
            ...initialTableParams.filters,
            uuid: paramsTasksUuids,
          },
        };
        // Не чистим url, потому что задачи не сохраняются в LS и сбросятся
      }

      setParams(localParams);

      // Fixme: Возможно нужно будет учитывать выбранные units_uuids в фильтрах, но пока не делаем
      let users = await fetchUserList();

      let sprintsList = await fetchSprintsList();

      // Проверяем необходимость обновления фильтров
      const newFilters = { ...localParams.filters };

      // Если ни один статус не выбран, то установить статусы по умолчанию ( все, кроме удаленных)
      if (!newFilters.status_uuid) {
        newFilters.status_uuid = statusesRes
          .filter((i) => !i.is_for_deletion && !i.is_for_cancellation)
          .map((i) => i.uuid);
      }

      // Если фильтр по рабочей группе, но ее нет в списке, сбросить фильтр
      if (
        newFilters.unit_uuid &&
        !unitsRes.some((u) => u.uuid === newFilters.unit_uuid)
      ) {
        newFilters.unit_uuid = null;
        users = await fetchUserList();
        sprintsList = await fetchSprintsList();
      }

      // Fixme: Дописать. Если в фильтре по дереву рабочих групп есть РГ, которых нет в дереве, сбросить фильтр

      // Если какой-то из статусов в фильтре выбран, но его нет в списке статусов, исключить его
      if (
        localParams.filters.status_uuid &&
        !localParams.filters.status_uuid.every((statusUuid) =>
          statusesRes.some((s) => s.uuid === statusUuid),
        )
      ) {
        newFilters.status_uuid = localParams.filters.status_uuid.filter(
          (statusUuid) => statusesRes.some((s) => s.uuid === statusUuid),
        );
      }

      // Если выбранный приоритет отсутствует в списке приоритетов, сбросить его
      if (
        localParams.filters.importance_uuid &&
        !prioritiesRes
          .filter(({ deleted }) => !deleted)
          .some((p) => p.uuid === localParams.filters.importance_uuid)
      ) {
        newFilters.importance_uuid = null;
      }

      // Если выбранный тип задачи отсутствует в списке типов задач, сбросить его
      if (
        localParams.filters.task_type_uuid &&
        !taskTypesRes
          .filter(({ deleted }) => !deleted)
          .some(
            (taskType) => taskType.uuid === localParams.filters.task_type_uuid,
          )
      ) {
        newFilters.task_type_uuid = null;
      }

      // Если выбранный автор отсутствует в списке сотрудников, сбросить его
      if (
        localParams.filters.author_uuid &&
        !users.some((e) => e.uuid === localParams.filters.author_uuid)
      ) {
        newFilters.author_uuid = null;
      }

      // Если выбранный исполнитель отсутствует в списке сотрудников, сбросить его
      if (
        localParams.filters.responsible_executor_uuid &&
        localParams.filters.responsible_executor_uuid !== NOT_FILLED_OPTION.uuid &&
        !users.some(
          (e) => e.uuid === localParams.filters.responsible_executor_uuid,
        )
      ) {
        newFilters.responsible_executor_uuid = null;
      }

      // Если выбранный спринт отсутствует в списке спринтов, сбросить его
      if (
        localParams.filters.sprint_uuid &&
        localParams.filters.sprint_uuid !== NOT_FILLED_OPTION.uuid &&
        !sprintsList.some((s) => s.uuid === localParams.filters.sprint_uuid)
      ) {
        newFilters.sprint_uuid = null;
      }

      const effectiveParams = {
        ...localParams,
        filters: {
          ...newFilters,
          ...(paramsCopyTx && { copy_transaction_id: paramsCopyTx }),
          ...(paramsTasksUuids?.length > 0 && { uuid: paramsTasksUuids }),
        },
      };

      const filtersChanged =
        JSON.stringify(newFilters) !== JSON.stringify(localParams.filters);
      if (filtersChanged) {
        setParams({
          ...localParams,
          filters: newFilters,
          pagination: { ...localParams.pagination, current: 1 },
        });
      }

      await fetchData(effectiveParams);

      if (paramsCopyTx) suppressNextFetch.current = true;
    } catch (error) {
      message.open({ type: "error", content: error.message, duration: 10 });
    } finally {
      setOptionsPending(false);
    }
  };

  // Получение списка пользователей для фильтров
  const fetchUserList = useCallback(
    async (unit_uuid) => {
      try {
        let list = [];
        if (isAdmins && !unit_uuid) {
          const { employees } = await getEmployeesOptions({
            is_dismissed: false,
          });
          list = employees.map(({ employee }) => employee);
        } else {
          const { members } = await getUnitEmployees({
            ...(unit_uuid && {
              unit_uuid: unit_uuid,
            }),
          });
          list = members;
        }

        const res = list.map((item) => {
          const { lastname, firstname, patronymic } = item;
          return {
            ...item,
            label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
          };
        });

        setEmployees(res);
        return res;
      } catch (error) {
        console.log(error);
      }
    },
    [isAdmins],
  );

  // Получение списка спринтов для фильтров
  const fetchSprintsList = async (unit_uuid) => {
    try {
      const sprintsRes = await getSprintsOptions({
        ...(unit_uuid && { unit_uuid }),
      });
      const res = sprintsRes.map((i) => ({ ...i, label: i.name }));
      setSprints(res);
      return res;
    } catch (error) {
      console.log(error);
    }
  };

  // Возвращает плоский список РГ
  const getFlatUnits = useMemo(() => {
    const flat = [];

    function traverse(nodes) {
      nodes.forEach((node) => {
        flat.push(node);
        if (Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }

    if (!unitsTree) return [];

    traverse(unitsTree);
    return flat;
  }, [unitsTree]);

  const updateFilters = useCallback(
    (localParams, newFilters, resetPage = false) => {
      const updatedFilters = { ...localParams.filters, ...newFilters };

      const updatedTableParams = {
        ...localParams,
        filters: updatedFilters,
        pagination: {
          ...localParams.pagination,
          current: resetPage ? 1 : localParams.pagination.current,
        },
      };

      setParams(updatedTableParams);
      fetchData(updatedTableParams);
    },
    [setParams, fetchData],
  );

  // Обработчик для смены фильтров
  const handleFilterChange = useCallback(
    async (name, val) => {
      const additionalFilters = {};

      if (name === FILTER_NAMES.UNITS_UUIDS) {
        additionalFilters.access_level = "all";
      }

      if (name === FILTER_NAMES.ACCESS_LEVEL) {
        additionalFilters.units_uuids =
          val === "all"
            ? []
            : val === "root"
              ? getFlatUnits.filter(({ readOnly }) => !readOnly)
              : getFlatUnits.filter(({ readOnly }) => readOnly);
      }

      updateFilters(
        tableParams,
        {
          [name]: val || null,
          ...additionalFilters,
        },
        true,
      );

    },
    [tableParams, updateFilters, getFlatUnits],
  );

  // Обработчик для смены временных фильтров в модалке
  const handleTempFilterChange = useCallback(
    (name, val) => {
      setTempValueFilters((prev) => {
        const additionalFilters = {};

        if (name === FILTER_NAMES.UNITS_UUIDS) {
          additionalFilters.access_level = "all";
        }

        if (name === FILTER_NAMES.ACCESS_LEVEL) {
          additionalFilters.units_uuids =
            val === "all"
              ? []
              : val === "root"
                ? getFlatUnits.filter(({ readOnly }) => !readOnly)
                : getFlatUnits.filter(({ readOnly }) => readOnly);
        }

        return {
          ...prev,
          [name]: val || null,
          ...additionalFilters,
        };
      });

    },
    [getFlatUnits],
  );

  // Обработчик для открытия модалки с фильтрами
  const openFiltersModal = () => {
    setTempValueFilters({
      ...tableParams.filters,
    });
    setIsFiltersModalShow(true);
  };

  // Применение выбранных фильтров из модалки
  const applyFilters = useCallback(() => {
    updateFilters(tableParams, { ...tempValueFilters }, true);
    setTempValueFilters(null);
    setIsFiltersModalShow(false);
  }, [tableParams, tempValueFilters, updateFilters]);

  // Очистка всех фильтров
  const clearFilters = () => {
    const updatedTableParams = {
      ...tableParams,
      filters: { ...initialTableParams.filters },
      pagination: {
        ...tableParams.pagination,
        current: 1, // Сбрасываем на первую страницу
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
    clearQueryParam?.(FILTER_NAMES.COPY_TRANSACTION_ID);
    clearQueryParam?.(FILTER_NAMES.TASKS_UUIDS);
  };

  const isAnyFilterApplied = useMemo(() => {
    const { filters } = tableParams;

    return Object.entries(filters).some(([key, value]) => {
      const initialValue = initialTableParams.filters[key];
      if (!initialValue && !value) return false;

      if (Array.isArray(value)) {
        return JSON.stringify(value) !== JSON.stringify(initialValue);
      }

      return value !== initialValue;
    });
  }, [tableParams, initialTableParams]);

  return {
    statuses,
    priorities,
    taskTypes,
    sprints,
    employees,
    units,
    unitsTree,
    unitData,
    optionsPending,
    tempValueFilters,
    isFiltersModalShow,
    setIsFiltersModalShow,
    handleFilterChange,
    handleTempFilterChange,
    openFiltersModal,
    applyFilters,
    clearFilters,
    isAnyFilterApplied,
  };
};
